import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useCities } from '../api';
import CityPickerModal from '../components/CityPickerModal';
import {
  loadLocationPreference,
  saveLocationPreference,
  prefToUserPos,
} from '../utils/locationPreference';
import { isPlausibleGeolocation } from '../utils/geo';
import { resolveGpsCityName } from '../utils/resolveGpsCityName';

const LocationPreferenceContext = createContext(null);

export function LocationPreferenceProvider({ children }) {
  const [userPos, setUserPos] = useState(null);
  const [preference, setPreference] = useState(() => loadLocationPreference());
  const [usedGeolocation, setUsedGeolocation] = useState(false);
  const [geoAttemptDone, setGeoAttemptDone] = useState(false);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const gpsNameLookupKey = useRef('');

  const { data: cities = [], isLoading: citiesLoading } = useCities();

  useEffect(() => {
    let cancelled = false;

    const finish = () => {
      if (!cancelled) setGeoAttemptDone(true);
    };

    const applySaved = (saved) => {
      if (cancelled) return;
      setPreference(saved);
      const pos = saved && saved.mode !== 'all' ? prefToUserPos(saved) : null;
      setUserPos(pos);
      if (saved?.mode === 'gps') setUsedGeolocation(true);
      finish();
    };

    const savedPref = loadLocationPreference();
    if (savedPref) {
      applySaved(savedPref);
      return () => {
        cancelled = true;
      };
    }

    const onSuccess = (pos) => {
      if (cancelled) return;
      if (!isPlausibleGeolocation(pos.coords)) {
        finish();
        return;
      }
      const lat = pos.coords.latitude;
      const lng = pos.coords.longitude;
      const pref = { mode: 'gps', latitude: lat, longitude: lng };
      saveLocationPreference(pref);
      setPreference(pref);
      setUsedGeolocation(true);
      setUserPos({ lat, lng });
      finish();
    };

    const onError = () => {
      if (cancelled) return;
      finish();
    };

    if (!navigator.geolocation) {
      finish();
      return () => {
        cancelled = true;
      };
    }

    navigator.geolocation.getCurrentPosition(onSuccess, onError, {
      enableHighAccuracy: false,
      timeout: 15000,
      maximumAge: 0,
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const applyCityPreference = useCallback((city) => {
    const pref = {
      _id: city._id,
      cityName: city.cityName,
      latitude: city.location?.latitude,
      longitude: city.location?.longitude,
    };
    saveLocationPreference(pref);
    setPreference(pref);
    const pos = prefToUserPos(pref);
    setUserPos(pos);
    setUsedGeolocation(false);
  }, []);

  const applyBrowseAll = useCallback(() => {
    saveLocationPreference({ mode: 'all' });
    setPreference({ mode: 'all' });
    setUserPos(null);
    setUsedGeolocation(false);
  }, []);

  const applyLiveLocation = useCallback((lat, lng, cityName) => {
    const name = typeof cityName === 'string' && cityName.trim() ? cityName.trim() : undefined;
    const pref = {
      mode: 'gps',
      latitude: lat,
      longitude: lng,
      ...(name ? { cityName: name } : {}),
    };
    saveLocationPreference(pref);
    setPreference(pref);
    setUserPos({ lat, lng });
    setUsedGeolocation(true);
  }, []);

  const showCityPicker = geoAttemptDone && !usedGeolocation && preference == null;

  const cityFilterActive = !usedGeolocation && preference && preference.mode !== 'all';
  const selectedCityId = cityFilterActive ? preference._id : null;
  const selectedCityName = (preference?.mode !== 'all' && preference?.cityName) || null;

  useEffect(() => {
    if (!userPos) return;
    if (preference?.mode === 'all') return;
    if (preference?.cityName) return;
    if (preference?.mode !== 'gps') return;

    const key = `${userPos.lat.toFixed(5)},${userPos.lng.toFixed(5)}:${cities.length}`;
    if (gpsNameLookupKey.current === key) return;
    gpsNameLookupKey.current = key;

    let cancelled = false;
    resolveGpsCityName(userPos.lat, userPos.lng, cities).then((name) => {
      if (cancelled || !name) return;
      setPreference((prev) => {
        if (!prev || prev.mode !== 'gps' || prev.cityName) return prev;
        const next = { ...prev, cityName: name };
        saveLocationPreference(next);
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [userPos, preference?.mode, preference?.cityName, cities]);

  const modalOpen = showCityPicker || userModalOpen;
  const allowDismiss = userModalOpen || !showCityPicker;

  const openLocationModal = useCallback(() => setUserModalOpen(true), []);
  const closeLocationModal = useCallback(() => setUserModalOpen(false), []);

  const handleDismissModal = useCallback(() => {
    setUserModalOpen(false);
  }, []);

  const handleConfirmCity = useCallback(
    (city) => {
      applyCityPreference(city);
    },
    [applyCityPreference]
  );

  const handleBrowseAll = useCallback(() => {
    applyBrowseAll();
  }, [applyBrowseAll]);

  const handleUseLiveLocation = useCallback(
    (lat, lng, cityName) => {
      applyLiveLocation(lat, lng, cityName);
    },
    [applyLiveLocation]
  );

  const value = useMemo(
    () => ({
      userPos,
      preference,
      usedGeolocation,
      geoAttemptDone,
      showCityPicker,
      applyCityPreference,
      applyBrowseAll,
      applyLiveLocation,
      cityFilterActive,
      selectedCityId,
      selectedCityName,
      openLocationModal,
      closeLocationModal,
    }),
    [
      userPos,
      preference,
      usedGeolocation,
      geoAttemptDone,
      showCityPicker,
      applyCityPreference,
      applyBrowseAll,
      applyLiveLocation,
      cityFilterActive,
      selectedCityId,
      selectedCityName,
      openLocationModal,
      closeLocationModal,
    ]
  );

  return (
    <LocationPreferenceContext.Provider value={value}>
      {children}
      <CityPickerModal
        isOpen={modalOpen}
        cities={cities}
        citiesLoading={citiesLoading}
        allowDismiss={allowDismiss}
        onDismiss={handleDismissModal}
        onConfirmCity={handleConfirmCity}
        onBrowseAll={handleBrowseAll}
        onUseLiveLocation={handleUseLiveLocation}
      />
    </LocationPreferenceContext.Provider>
  );
}

export function useLocationPreference() {
  const ctx = useContext(LocationPreferenceContext);
  if (!ctx) {
    throw new Error('useLocationPreference must be used within LocationPreferenceProvider');
  }
  return ctx;
}
